import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, Download, ShieldAlert } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

// @seo-check: noindex-ok
export const Route = createFileRoute("/admin/downloads")({
  head: () => ({
    meta: [
      { title: "Downloads do APK | MEI Toolbox" },
      {
        name: "description",
        content: "Área administrativa: contagem de downloads reais do APK do MEI Toolbox.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://meitoolbox.com.br/admin/downloads" }],
  }),
  component: AdminDownloadsPage,
});

type Row = {
  id: string;
  created_at: string;
  user_agent: string | null;
  referer: string | null;
  country: string | null;
};

function AdminDownloadsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [counts, setCounts] = useState({ total: 0, day: 0, week: 0, month: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate({ to: "/auth" as never });
      return;
    }
    (async () => {
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();
      const admin = !!roleData;
      setIsAdmin(admin);
      if (!admin) {
        setLoading(false);
        return;
      }
      const [{ count: total }, recent] = await Promise.all([
        supabase.from("apk_downloads").select("*", { count: "exact", head: true }),
        supabase
          .from("apk_downloads")
          .select("id, created_at, user_agent, referer, country")
          .order("created_at", { ascending: false })
          .limit(50),
      ]);

      const now = Date.now();
      const day = new Date(now - 24 * 3600 * 1000).toISOString();
      const week = new Date(now - 7 * 24 * 3600 * 1000).toISOString();
      const month = new Date(now - 30 * 24 * 3600 * 1000).toISOString();

      const [d, w, m] = await Promise.all([
        supabase
          .from("apk_downloads")
          .select("*", { count: "exact", head: true })
          .gte("created_at", day),
        supabase
          .from("apk_downloads")
          .select("*", { count: "exact", head: true })
          .gte("created_at", week),
        supabase
          .from("apk_downloads")
          .select("*", { count: "exact", head: true })
          .gte("created_at", month),
      ]);

      setCounts({
        total: total ?? 0,
        day: d.count ?? 0,
        week: w.count ?? 0,
        month: m.count ?? 0,
      });
      setRows((recent.data as Row[]) ?? []);
      setLoading(false);
    })();
  }, [user, authLoading, navigate]);

  if (authLoading || loading) {
    return (
      <div className="container mx-auto px-4 py-20 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="container mx-auto px-4 py-20 max-w-md text-center">
        <ShieldAlert className="h-10 w-10 text-destructive mx-auto mb-3" />
        <h1 className="text-2xl font-bold">Acesso restrito</h1>
        <p className="text-muted-foreground mt-2">
          Você precisa ser administrador para ver esta página.
        </p>
      </div>
    );
  }

  const stats = [
    { label: "Total", value: counts.total },
    { label: "Últimas 24h", value: counts.day },
    { label: "Últimos 7 dias", value: counts.week },
    { label: "Últimos 30 dias", value: counts.month },
  ];

  return (
    <div className="container mx-auto px-4 py-10 max-w-5xl">
      <div className="flex items-center gap-3 mb-6">
        <Download className="h-6 w-6 text-primary" />
        <h1 className="font-display text-2xl sm:text-3xl font-bold">Downloads do APK</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {stats.map((s) => (
          <div key={s.label} className="rounded-2xl border border-border bg-card p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</div>
            <div className="font-display text-3xl font-bold text-primary mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      <h2 className="font-bold mb-3">Últimos 50 downloads</h2>
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3">País</th>
                <th className="px-4 py-3">Origem</th>
                <th className="px-4 py-3">Dispositivo</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">
                    Nenhum download registrado ainda.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-4 py-3 whitespace-nowrap">
                    {new Date(r.created_at).toLocaleString("pt-BR")}
                  </td>
                  <td className="px-4 py-3">{r.country ?? "—"}</td>
                  <td className="px-4 py-3 max-w-[240px] truncate" title={r.referer ?? ""}>
                    {r.referer ?? "—"}
                  </td>
                  <td className="px-4 py-3 max-w-[280px] truncate" title={r.user_agent ?? ""}>
                    {r.user_agent ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
